// Force CI
import { fetchAuthSession, fetchUserAttributes, getCurrentUser } from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import { StopListenerCallback } from '@aws-amplify/core/dist/esm/Hub/types';
import { BottomBanner } from '@chiroup/components/BottomBanner';
import { Loading } from '@chiroup/components/Loading';
import { lazy, Suspense, useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import AdminUserDetail from './components/admin-users/AdminUserDetail';
import AdminUserList from './components/admin-users/AdminUserList';
import Analytics from './components/analytics/Analytics';
import BulkCampaignApprovals from './components/campaign-approvals/BulkCampaignApprovals';
import { ClinicMap } from './components/clinic-map';
import ClinicalSkills from './components/clinical-skills/ClinicalSkills';
import Clinics from './components/clinics/Clinics';
import { addScript } from './components/common/helpers/addScript';
import RequirePermission from './components/common/RequirePermission';
import Operations from './components/connection-api/Operations';
import OpsConsole from './components/connection-api/OpsConsole';
import Queues from './components/connection-api/Queues';
import CrossoverRepair from './components/developer-tools/crossover-repair/CrossoverRepair';
import DejavooDiagnostics from './components/developer-tools/dejavoo-diagnostics/DejavooDiagnostics';
import PatientPanel from './components/developer-tools/patient-panel/PatientPanel';
import EraPanel from './components/developer-tools/transaction-debug/EraPanel';
import InvoicePanel from './components/developer-tools/transaction-debug/InvoicePanel';
import TransactionDebug from './components/developer-tools/transaction-debug/TransactionDebugger';
import DeviceBlocklist from './components/device-blocklist/DeviceBlocklist';
import Disabilities from './components/disabilities/Disabities';
import Flags from './components/flags/Flags';
import HomePage from './components/home-page/HomePage';
import TrialAnalyticsPage from './components/home-page/TrialAnalyticsPage';
import TrialExperienceEditor from './components/home-page/TrialExperienceEditor';
import Container from './components/layout/Container';
import LoginContentEditor from './components/login-content/LoginContentEditor';
import Marketing from './components/marketing/Marketing';
import NewUserDashboard from './components/new-user-dashboard/NewUserDashboard';
import FormsLibrary from './components/practice-resources/forms-library/FormsLibrary';
import MarketingCampaigns from './components/practice-resources/marketing/MarketingCampaigns';
import ResearchAnalytics from './components/research-analytics/ResearchAnalytics';
import ResearchList from './components/research/ResearchList';
import SecureCases from './components/secure-cases/SecureCases';
import StudentVerification from './components/students/StudentVerification';
import Tests from './components/tests/Tests';
import Users from './components/users/Users';

const HomePageV2Editor = lazy(() => import('./components/home-page-v2/HomePageV2Editor'));
const HomePageAnalyticsPage = lazy(
  () => import('./components/home-page-v2/Analytics/HomePageAnalyticsPage')
);

const LoggedOut = lazy(() => import('./components/logged-out/LoggedOut'));
const AskChiroUp = lazy(() => import('./components/ask-chiroup/AskChiroUp'));
const ReferralsDashboard = lazy(() => import('./components/referrals/ReferralsDashboard'));

const queryClient = new QueryClient();

function App() {
  const [authState, setAuthState] = useState<string>();
  const [user, setUser] = useState<object | undefined>();

  useEffect(() => {
    let isDestroyed = false;
    let stopListener: StopListenerCallback;

    const doIt = async () => {
      const session = await fetchAuthSession().catch(() => undefined);
      const scope = session?.tokens?.accessToken.payload.scope;
      // Google OAuth sessions carry profile claims but cannot call Cognito GetUser
      // unless they have its reserved self-service scope.
      const canFetchAttributes =
        typeof scope === 'string' && scope.split(' ').includes('aws.cognito.signin.user.admin');
      const attr = canFetchAttributes ? await fetchUserAttributes().catch(err => {}) : undefined;
      const tokenEmail = session?.tokens?.idToken?.payload.email;
      const currentUser = await getCurrentUser().catch(err => {});

      if (currentUser && !isDestroyed) {
        setAuthState('authenticated');
        setUser({
          ...currentUser,
          email: attr?.email || (typeof tokenEmail === 'string' ? tokenEmail : undefined),
        });
      }

      stopListener = Hub.listen('auth', data => {
        if (!isDestroyed) {
          if (data.payload.event === 'tokenRefresh_failure') {
            setAuthState('signedOut');
            // setInactivityModalOpen(true);
          } else if (data.payload.event === 'signedOut') {
            setAuthState('signedOut');
          } else if (data.payload.event === 'signedIn') {
            setAuthState('authenticated');
            setUser(data.payload.data);
          }
        }
      });
    };

    doIt();

    return () => {
      isDestroyed = true;
      stopListener?.();
    };
  }, []);

  useEffect(() => {
    addScript(
      import.meta.env.VITE_STAGE === 'prod'
        ? 'https://js.authorize.net/v1/Accept.js'
        : 'https://jstest.authorize.net/v1/Accept.js'
    );
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {authState === 'authenticated' && user ? (
          <Container user={user}>
            <Suspense fallback={<Loading size={5} />}>
              <Routes>
                <Route
                  path="/"
                  element={
                    <RequirePermission permission="analytics.view">
                      <Analytics />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/clinic-map"
                  element={
                    <RequirePermission permission="clinic_map.view">
                      <ClinicMap />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/admin-users"
                  element={
                    <RequirePermission permission="admin_users.view">
                      <AdminUserList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/admin-users/:id"
                  element={
                    <RequirePermission permission="admin_users.view">
                      <AdminUserDetail />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/device-blocklist"
                  element={
                    <RequirePermission permission="device_blocklist.view">
                      <DeviceBlocklist />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/campaign-approvals"
                  element={
                    <RequirePermission permission="campaign.approve_bulk_sms">
                      <BulkCampaignApprovals />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/users/*"
                  element={
                    <RequirePermission permission="users.view">
                      <Users />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/referrals"
                  element={
                    <RequirePermission permission="referrals.view">
                      <ReferralsDashboard />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/clinics/*"
                  element={
                    <RequirePermission permission="clinic.view">
                      <Clinics user={user} />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/secure-cases/*"
                  element={
                    <RequirePermission permission="secure_cases.view">
                      <SecureCases />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/research"
                  element={
                    <RequirePermission permission="research.view">
                      <ResearchList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/research/analytics"
                  element={
                    <RequirePermission permission="research.analytics">
                      <ResearchAnalytics />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/analytics/*"
                  element={
                    <RequirePermission permission="analytics.view">
                      <Analytics />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/tests/*"
                  element={
                    <RequirePermission permission="tests.view">
                      <Tests />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/disabilities"
                  element={
                    <RequirePermission permission="disabilities.view">
                      <Disabilities />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/home-page"
                  element={
                    <RequirePermission permission="home_page.view">
                      <HomePage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/home-page/trial-experience"
                  element={
                    <RequirePermission permission="home_page.view">
                      <TrialExperienceEditor />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/home-page/trial-experience/analytics"
                  element={
                    <RequirePermission permission="home_page.view">
                      <TrialAnalyticsPage />
                    </RequirePermission>
                  }
                />
                {/* Legacy routes redirect to the merged Trial Experience editor so
                    bookmarks keep working. */}
                <Route
                  path="/home-page/trial"
                  element={<Navigate to="/home-page/trial-experience" replace />}
                />
                <Route
                  path="/home-page/trial-tours"
                  element={<Navigate to="/home-page/trial-experience" replace />}
                />
                <Route
                  path="/home-page-v2"
                  element={
                    <RequirePermission permission="home_page.view">
                      <HomePageV2Editor />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/home-page-v2/analytics"
                  element={
                    <RequirePermission permission="home_page.view">
                      <HomePageAnalyticsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/login-content"
                  element={
                    <RequirePermission permission="login_screen.view">
                      <LoginContentEditor />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/new-user-dashboard"
                  element={
                    <RequirePermission permission="new_user_dashboard.view">
                      <NewUserDashboard />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/student-verification"
                  element={
                    <RequirePermission permission="student_verification.view">
                      <StudentVerification />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/flags"
                  element={
                    <RequirePermission permission="flags.view">
                      <Flags />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/ask-chiroup"
                  element={
                    <RequirePermission permission="ask_chiroup.use">
                      <AskChiroUp />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/practice-resources/forms-library/*"
                  element={
                    <RequirePermission permission="practice_resources.view">
                      <FormsLibrary user={user} />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/practice-resources/marketing-campaigns/*"
                  element={
                    <RequirePermission permission="practice_resources.view">
                      <MarketingCampaigns />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/marketing"
                  element={
                    <RequirePermission permission="marketing.view">
                      <Marketing />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/clinical-skills/*"
                  element={
                    <RequirePermission permission="clinical_skills.view">
                      <ClinicalSkills />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/connection-api/queues"
                  element={
                    <RequirePermission permission="platform.manage_connector_credentials">
                      <Queues />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/connection-api/operations"
                  element={
                    <RequirePermission permission="platform.manage_connector_credentials">
                      <Operations />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/connection-api/*"
                  element={
                    <RequirePermission permission="platform.manage_connector_credentials">
                      <OpsConsole />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/developer-tools/transaction-debug/*"
                  element={
                    <RequirePermission permission="devtools.transaction_debug">
                      <TransactionDebug />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/developer-tools/invoice-panel/*"
                  element={
                    <RequirePermission permission="devtools.invoices">
                      <InvoicePanel />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/developer-tools/crossover-repair"
                  element={
                    <RequirePermission permission="devtools.invoices">
                      <CrossoverRepair />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/developer-tools/era-panel/*"
                  element={
                    <RequirePermission permission="devtools.eras">
                      <EraPanel />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/developer-tools/patient-panel/*"
                  element={
                    <RequirePermission permission="devtools.transaction_debug">
                      <PatientPanel />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/developer-tools/dejavoo-diagnostics/*"
                  element={
                    <RequirePermission permission="devtools.dejavoo_diagnostics">
                      <DejavooDiagnostics />
                    </RequirePermission>
                  }
                />
              </Routes>
            </Suspense>
          </Container>
        ) : (
          <Suspense fallback={<Loading size={5} />}>
            <LoggedOut />
          </Suspense>
        )}
      </BrowserRouter>
      {/* <InactivityModal
        open={inactivityModalOpen}
        setOpen={setInactivityModalOpen}
        loggedOut={authState === 'signedOut'}
        setIsIdle={setIsIdle}
      /> */}
      {import.meta.env.VITE_STAGE === 'sandbox' && (
        <BottomBanner message="You are in sandbox mode. Do not use real data." />
      )}
    </QueryClientProvider>
  );
}

export default App;
